import React, { useEffect, useState } from 'react';
import GaugeWidget from './GaugeWidget';
import TableWidget from './TableWidget';
import ServiceStatusWidget from './ServiceStatusWidget';
import ChartWidget from './ChartWidget';
import CountWidget from './CountWidget';
import ReportWidget from './ReportWidget';
import ScorecardWidget from './ScorecardWidget';
import StatementWidget from './StatementWidget';
import useStore from '../../store/useStore';
import { useStaleThresholdMinutes } from './wallSection';
import FreshnessBadge from './FreshnessBadge';
import MetricHistoryModal from './MetricHistoryModal';
import { metricsAPI } from '../../services/api';

const SPARKLINE_TYPES = new Set(['count', 'gauge']);
const DEFAULT_SPARKLINE_MINUTES = 24 * 60;
const SPARKLINE_POINTS = 60;

const numberOf = (value) => {
  const n = Number(value && typeof value === 'object' ? value.current : value);
  return Number.isFinite(n) ? n : null;
};

const WidgetFactory = ({ widget }) => {
  const { widget_kind, metric_key, widget_type, config, metric_config } = widget;
  // A title set on this widget overrides the metric's display name.
  const display_name = config?.title || widget.display_name;
  const staleThresholdMinutes = useStaleThresholdMinutes();
  const companyId = useStore((state) => state.currentCompany?.id);
  const setMetricHistory = useStore((state) => state.setMetricHistory);
  const [showHistory, setShowHistory] = useState(false);

  // Subscribe ONLY to this specific metric, not the entire metrics object
  const metricData = useStore((state) => state.metrics[metric_key]);
  const history = useStore((state) => state.metricHistory[metric_key]);
  // Active alert on this metric (worst severity first is how the store keeps them)
  const alert = useStore((state) => state.alerts.find((a) => a.kind === 'live' && a.metricKey === metric_key));

  // Get configuration from metric_config or widget config
  const widgetConfig = { ...metric_config, ...config };
  const sparklineMinutes = Number(widgetConfig.sparklineMinutes) || DEFAULT_SPARKLINE_MINUTES;
  const wantsSparkline = widget_kind !== 'dataset' && widget_kind !== 'kpi' && widget_kind !== 'statement' && SPARKLINE_TYPES.has(widget_type) && widgetConfig.sparkline !== false;

  // Seed the sparkline from the pushed history once; live pushes append via the store.
  useEffect(() => {
    if (!wantsSparkline || !companyId || !metric_key || (history && history.length > 1)) return undefined;
    let cancelled = false;
    const from = new Date(Date.now() - sparklineMinutes * 60 * 1000).toISOString();
    metricsAPI.getHistory(metric_key, { companyId, limit: SPARKLINE_POINTS, from })
      .then((res) => {
        if (cancelled || !res?.success) return;
        const points = (res.data || [])
          .map((m) => ({ t: m.timestamp, v: numberOf(m.value) }))
          .filter((p) => p.v !== null)
          .sort((a, b) => new Date(a.t) - new Date(b.t));
        if (points.length) setMetricHistory(metric_key, points);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsSparkline, companyId, metric_key, sparklineMinutes]);

  // Dataset-backed report widgets fetch their own data via the datasets API —
  // they don't subscribe to the live metrics slice.
  if (widget_kind === 'dataset') {
    return <ReportWidget widget={widget} />;
  }
  if (widget_kind === 'kpi') {
    return <ScorecardWidget widget={widget} />;
  }
  if (widget_kind === 'statement') {
    return <StatementWidget widget={widget} />;
  }

  // Parse value from metric data
  const getValue = () => {
    if (!metricData || !metricData.value) return null;

    try {
      // If value is already an object, return it
      if (typeof metricData.value === 'object') {
        return metricData.value;
      }
      // If value is a JSON string, parse it
      if (typeof metricData.value === 'string') {
        return JSON.parse(metricData.value);
      }
      return metricData.value;
    } catch (error) {
      console.error('Error parsing metric value:', error);
      return metricData.value;
    }
  };

  const value = getValue();
  const windowStart = Date.now() - sparklineMinutes * 60 * 1000;
  const sparkline = wantsSparkline
    ? (history || []).filter((p) => !p.t || new Date(p.t).getTime() >= windowStart).map((p) => p.v)
    : null;

  const badge = (
    <FreshnessBadge kind="live" at={metricData?.timestamp} staleAfterMs={staleThresholdMinutes * 60 * 1000} className="freshness-live-hint" />
  );
  const shell = (node) => (
    <div className={`widget-shell ${alert ? `widget-shell--alert widget-shell--alert-${alert.severity}` : ''}`}>
      {node}
      {badge}
      {alert && (
        <span className={`widget-alert-badge widget-alert-badge--${alert.severity}`} title={`${alert.name}: ${alert.metricKey} ${alert.operator} ${alert.threshold}`} data-testid="widget-alert-badge">
          ⚠ {alert.severity}
        </span>
      )}
      <button type="button" className="widget-history-btn" title="Recent history" aria-label={`History of ${display_name}`} onClick={() => setShowHistory(true)}>⏱</button>
      {showHistory && <MetricHistoryModal metricKey={metric_key} title={display_name} onClose={() => setShowHistory(false)} />}
    </div>
  );

  // Render appropriate widget based on type
  switch (widget_type) {
    case 'gauge':
      return shell(
        <GaugeWidget
          title={display_name}
          value={value?.current ?? value}
          min={widgetConfig?.min ?? 0}
          max={widgetConfig?.max ?? 100}
          thresholds={widgetConfig?.thresholds || []}
          history={sparkline}
        />
      );

    case 'table':
      return shell(
        <TableWidget
          title={display_name}
          data={value}
          columns={widgetConfig?.columns || ['Item', 'Count']}
        />
      );

    case 'service_status':
      return shell(
        <ServiceStatusWidget
          title={display_name}
          services={value || []}
          config={widgetConfig}
        />
      );

    case 'chart':
    case 'line_chart':
    case 'area_chart':
      return shell(
        <ChartWidget
          title={display_name}
          data={value || []}
          chartType={widget_type === 'area_chart' ? 'area' : 'line'}
          dataKey={widgetConfig?.dataKey || 'value'}
        />
      );

    case 'count':
      return shell(
        <CountWidget
          title={display_name}
          value={value?.current ?? value ?? 0}
          unit={widgetConfig?.unit || ''}
          trend={value?.trend ?? null}
          config={widgetConfig}
          history={sparkline}
        />
      );

    default:
      return (
        <div className="widget">
          <div className="widget-header">
            <span className="widget-title">{display_name}</span>
          </div>
          <div className="widget-body">
            <div style={{ color: 'var(--widget-text-secondary)' }}>Unknown widget type: {widget_type}</div>
          </div>
        </div>
      );
  }
};

export default WidgetFactory;
